import { useSignal } from '@preact/signals'
import { useLayoutEffect, useRef } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { age, timeOfDay } from '../../../client/format.ts'
import { sessionRow, type RowFacts } from '../../../client/session-row.ts'
import { read, subscribe } from '../composer/drafts.ts'
import { Glyph, Icon } from '../ui/icon.tsx'
import { Badge, MetaLine } from '../ui/controls.tsx'
import { useLongPress } from '../ui/gestures.ts'
import { keys } from '../ui/layers.ts'
import { activationCause, type MotionCause } from '../ui/spring.ts'
import { useScope } from '../ui/scope.tsx'
import type { DisplayEntry, SessionsDisplay } from './display.ts'
import type { SessionRow as ProjectedRow } from './contracts.ts'
import type { PageClock } from './clock.ts'

export interface SessionRowProps {
  display: SessionsDisplay
  entry: Extract<DisplayEntry, { kind: 'row' }>
  top: number
  height: number
  onOpen(ref: string): void
  onMenu(ref: string, anchor: HTMLElement, cause: MotionCause, rendered: Readonly<ProjectedRow>, openingPointerId?: number): void
  history: boolean
  query?: string
  visible: boolean
  clock: PageClock
  factsEnabled?: boolean
  lastInput?: boolean
  showBackend?: boolean
}

function marked(text: string, query = ''): ComponentChildren {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return text
  const lower = text.toLocaleLowerCase(), pieces: ComponentChildren[] = []
  let from = 0, at: number
  while ((at = lower.indexOf(needle, from)) >= 0) {
    pieces.push(text.slice(from, at), <mark>{text.slice(at, at + needle.length)}</mark>)
    from = at + needle.length
  }
  pieces.push(text.slice(from))
  return pieces
}

/** Only this component reads its row cell; structure and the parent never subscribe to row facts. */
export function SessionRow({ display, entry, top, height, onOpen, onMenu, history, query, visible, clock, factsEnabled = true, lastInput = false, showBackend = true }: SessionRowProps) {
  const cell = display.rows.item(entry.ref), value = visible ? cell.value : cell.peek()
  const source = value?.row as ProjectedRow | undefined
  const row: ProjectedRow | undefined = source && !factsEnabled ? (({ activity, outcome, agentsWorking, tasks, background, waitingText, ...base }) => base)(source) : source
  const owner = useScope(), target = useRef<HTMLAnchorElement>(null)
  const draft = useSignal(read(entry.ref))
  const callbacks = useRef({ onOpen, onMenu, row })
  callbacks.current = { onOpen, onMenu, row }
  const openMenu = (anchor: HTMLElement, cause: MotionCause, openingPointerId?: number) => {
    if (callbacks.current.row && display.canOpen(entry.ref)) callbacks.current.onMenu(entry.ref, anchor, cause, callbacks.current.row, openingPointerId)
  }
  const press = useLongPress(event => openMenu(event.currentTarget, 'gesture', event.pointerId))
  const since = visible && row?.state === 'running' ? row.activity?.since : undefined
  const now = Number.isFinite(since) ? clock.item(entry.ref).value : Date.now()
  useLayoutEffect(() => {
    draft.value = read(entry.ref)
    return subscribe(entry.ref, () => { draft.value = read(entry.ref) })
  }, [entry.ref])
  useLayoutEffect(() => clock.watch(entry.ref, since), [clock, entry.ref, since])
  useLayoutEffect(() => () => clock.forget(entry.ref), [clock, entry.ref])
  useLayoutEffect(() => keys(owner, event => {
    if (event.key === '.' || event.key === 'ContextMenu' || event.shiftKey && event.key === 'F10') {
      if (!event.repeat) openMenu(target.current!, 'keyboard')
      return true
    }
    if (event.key === 'Enter') {
      if (!event.repeat && display.canOpen(entry.ref)) callbacks.current.onOpen(entry.ref)
      return true
    }
  }, { target: target.current! }), [owner, display, entry.ref])
  if (!row) return null
  const words = sessionRow(row, now, { showBackend }), unread = (row.unread ?? 0) > 0
  const draftWords = visible ? draft.value : draft.peek()
  const items = [{ key: 'state', node: <span class="sessions-row-state" data-tone={words.tone}>{words.phrase}</span>, priority: -1, shrink: true, cutLast: true }, ...words.metadata.map((text, index) => {
    const directory = index === words.metadata.length - 1 && !!row.cwd
    return { key: `meta-${index}`, node: directory ? <span class="sessions-row-directory" title={row.cwd}>{text}</span> : text, priority: directory ? words.metadata.length + 1 : words.metadata.length - index }
  })]
  return <div class="session-row sessions-row" role="listitem" data-ref={row.ref} data-removed={value?.removed || undefined} data-state={row.state} style={{ position: 'absolute', top, height, width: '100%' }}>
    <a ref={target} class="sessions-row-open" href={`#/s/${encodeURIComponent(row.ref)}`} aria-disabled={value?.removed || undefined} tabIndex={value?.removed ? -1 : 0}
      {...press}
      onContextMenu={event => { event.preventDefault(); openMenu(event.currentTarget, 'pointer') }}
      onClick={event => { event.preventDefault(); if (display.canOpen(row.ref)) onOpen(row.ref) }}>
      <span class="sessions-row-glyph"><Glyph status={words.status} /></span>
      <span class="sessions-row-text">
        <span class="sessions-row-line1"><span class={`sessions-row-title ${unread ? 'type-headline' : 'type-body-ui'}`} title={row.title}>{marked(row.title || 'Untitled session', query)}</span><span class="sessions-row-age type-footnote">{history ? timeOfDay(row.orderAt) : age(row.orderAt, now)}</span>{unread && <span class="sessions-row-unread" aria-label={`${row.unread} unread updates`}><Badge count={row.unread!} neutral={row.unread! > 1} /></span>}</span>
        <span class="sessions-row-line2">{draftWords ? <span class="sessions-row-draft type-footnote">Draft: {draftWords}</span> : <MetaLine items={items} />}</span>
      </span>
    </a>
    <button type="button" class="sessions-row-menu ui-icon-button" aria-label={`Actions for ${row.title || 'Untitled session'}`} disabled={value?.removed} onClick={event => { event.stopPropagation(); openMenu(event.currentTarget, activationCause(event)) }}><Icon name="more" /></button>
  </div>
}
